import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { ComboboxInput } from '@/components/ui/ComboboxInput';
import especialidadesJson from '@/assets/json/especialidades.json';
import { getErrorMessage } from '@/lib/errors';
import { useAuthStore, ROL } from '@/store/auth.store';
import type { PerfilProfesional } from '@/lib/modulosPerfil';

const ESPECIALIDADES: string[] = (especialidadesJson as { Nombre: string }[])
  .map(e => e.Nombre)
  .sort();

const inputCls =
  'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

// Mismo grupo usado en frontend/src/components/layout/modules.config.ts y en
// backend/src/modules/personal/personal.controller.ts (ADMIN_ROLES) — único grupo que
// puede setear perfilProfesional/facultadPrescribir (ver spec granularidad-perfil-profesional).
const ADMIN_ROLES = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN];

const PERFIL_PROFESIONAL_OPTIONS: { value: PerfilProfesional; label: string }[] = [
  { value: 'medico_general', label: 'Médico general' },
  { value: 'odontologia', label: 'Odontología' },
  { value: 'fisioterapia', label: 'Fisioterapia' },
  { value: 'nutricion', label: 'Nutrición' },
  { value: 'psicologia_salud_mental', label: 'Psicología / Salud mental' },
  { value: 'administrativo', label: 'Administrativo' },
];

// Perfiles con facultad de prescribir por defecto — debe reflejar
// PERFILES_PRESCRIPCION_DEFAULT en backend/src/modules/personal/personal.service.ts.
const PERFILES_PRESCRIPCION_DEFAULT: PerfilProfesional[] = ['medico_general', 'odontologia'];

export interface EspecialidadItem {
  id: string;
  nombre: string;
  institucionFormadora: string | null;
  cedulaEspecialidad: string | null;
  esPrincipal: boolean;
  perfilProfesional: PerfilProfesional | null;
  facultadPrescribir: boolean | null;
}

interface Props {
  usuarioId: string;
}

const emptyAdd = {
  nombre: '', institucionFormadora: '', cedulaEspecialidad: '',
  perfilProfesional: '' as PerfilProfesional | '', facultadPrescribir: false,
};

export function EspecialidadesManager({ usuarioId }: Props) {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const esAdmin = !!(usuario?.rolId && (ADMIN_ROLES as string[]).includes(usuario.rolId));
  const queryKey = ['especialidades-medico', usuarioId];

  const { data: items = [], isLoading } = useQuery<EspecialidadItem[]>({
    queryKey,
    queryFn: () => api.get(`/api/usuarios/${usuarioId}/especialidades`).then(r => r.data),
    enabled: !!usuarioId,
  });

  const [addForm, setAddForm] = useState(emptyAdd);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(emptyAdd);

  const addMut = useMutation({
    mutationFn: (dto: Record<string, any>) =>
      api.post(`/api/usuarios/${usuarioId}/especialidades`, dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setAddForm(emptyAdd);
      setShowAdd(false);
      toast.success('Especialidad agregada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al agregar especialidad')),
  });

  const editMut = useMutation({
    mutationFn: ({ id, ...dto }: { id: string } & Record<string, any>) =>
      api.patch(`/api/usuarios/${usuarioId}/especialidades/${id}`, dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setEditId(null);
      toast.success('Especialidad actualizada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/usuarios/${usuarioId}/especialidades/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast.success('Especialidad eliminada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar')),
  });

  const principalMut = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/api/usuarios/${usuarioId}/especialidades/${id}/principal`, {}).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast.success('Especialidad principal actualizada');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al cambiar principal')),
  });

  function startEdit(item: EspecialidadItem) {
    setEditId(item.id);
    setEditForm({
      nombre: item.nombre,
      institucionFormadora: item.institucionFormadora ?? '',
      cedulaEspecialidad: item.cedulaEspecialidad ?? '',
      perfilProfesional: item.perfilProfesional ?? '',
      facultadPrescribir: item.facultadPrescribir ?? false,
    });
  }

  // perfilProfesional/facultadPrescribir solo se envían si el usuario actual es admin — el
  // backend los descarta igual si no lo es, pero un no-admin ni siquiera ve estos campos.
  function buildDto(form: typeof emptyAdd) {
    const { perfilProfesional, facultadPrescribir, ...base } = form;
    if (!esAdmin) return base;
    return { ...base, perfilProfesional: perfilProfesional || null, facultadPrescribir };
  }

  function handleAdd() {
    if (!addForm.nombre.trim()) return toast.error('Selecciona una especialidad');
    addMut.mutate(buildDto(addForm));
  }

  function handleSaveEdit() {
    if (!editForm.nombre.trim()) return toast.error('El nombre es requerido');
    editMut.mutate({ id: editId!, ...buildDto(editForm) });
  }

  if (isLoading) {
    return <p className="text-sm text-gray-400 text-center py-4">Cargando especialidades...</p>;
  }

  return (
    <div className="space-y-2">
      {/* Lista */}
      {items.length === 0 && !showAdd && (
        <p className="text-sm text-gray-400 text-center py-3">Sin especialidades registradas</p>
      )}

      {items.map(item => (
        <div
          key={item.id}
          className={`rounded-xl border p-3 transition-colors ${
            item.esPrincipal
              ? 'border-primary-200 bg-primary-50/40'
              : 'border-gray-100 bg-gray-50/50'
          }`}
        >
          {editId === item.id ? (
            <div className="space-y-2">
              <ComboboxInput
                options={ESPECIALIDADES}
                value={editForm.nombre}
                onChange={v => setEditForm(p => ({ ...p, nombre: v }))}
                placeholder="Especialidad"
                className={inputCls}
                freeText={false}
              />
              <input
                value={editForm.institucionFormadora}
                onChange={e => setEditForm(p => ({ ...p, institucionFormadora: e.target.value }))}
                placeholder="Institución formadora (ej. UNAM, BUAP...)"
                className={inputCls}
                maxLength={255}
              />
              <input
                value={editForm.cedulaEspecialidad}
                onChange={e => setEditForm(p => ({ ...p, cedulaEspecialidad: e.target.value }))}
                placeholder="Cédula de especialidad"
                className={inputCls}
                maxLength={100}
              />
              {esAdmin && (
                <div className="space-y-2 pt-1 border-t border-gray-100">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Perfil profesional (determina qué funciones ve en el expediente)</label>
                    <select
                      className={inputCls}
                      value={editForm.perfilProfesional}
                      onChange={e => {
                        const perfil = e.target.value as PerfilProfesional | '';
                        setEditForm(p => ({
                          ...p, perfilProfesional: perfil,
                          facultadPrescribir: perfil ? PERFILES_PRESCRIPCION_DEFAULT.includes(perfil as PerfilProfesional) : p.facultadPrescribir,
                        }));
                      }}
                    >
                      <option value="">Sin clasificar (ve todo, por defecto)</option>
                      {PERFIL_PROFESIONAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none">
                    <input type="checkbox" checked={editForm.facultadPrescribir}
                      onChange={e => setEditForm(p => ({ ...p, facultadPrescribir: e.target.checked }))} />
                    Facultad de prescripción (habilita recetas y medicamentos activos)
                  </label>
                </div>
              )}
              <p className="text-xs text-text-secondary">
                Datos obligatorios ante COFEPRIS para la emisión de recetas digitales.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={editMut.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-colors disabled:opacity-60"
                >
                  <Check size={12} /> Guardar
                </button>
                <button
                  type="button"
                  onClick={() => setEditId(null)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <X size={12} /> Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {item.esPrincipal && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                      <Star size={10} className="fill-primary-500 text-primary-500" />
                      Principal
                    </span>
                  )}
                  <span className="text-sm font-medium text-text-primary">{item.nombre}</span>
                </div>
                {item.institucionFormadora && (
                  <p className="text-xs text-gray-500 mt-0.5">Institución formadora: {item.institucionFormadora}</p>
                )}
                {item.cedulaEspecialidad && (
                  <p className="text-xs text-gray-500 mt-0.5">Cédula de especialidad: {item.cedulaEspecialidad}</p>
                )}
              </div>

              <div className="flex items-center gap-0.5 shrink-0">
                {!item.esPrincipal && (
                  <button
                    type="button"
                    title="Hacer principal"
                    onClick={() => principalMut.mutate(item.id)}
                    disabled={principalMut.isPending}
                    className="p-1.5 text-gray-400 hover:text-yellow-500 transition-colors rounded-lg hover:bg-yellow-50"
                  >
                    <Star size={14} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => startEdit(item)}
                  className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors rounded-lg hover:bg-primary-50"
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => deleteMut.mutate(item.id)}
                  disabled={deleteMut.isPending}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Formulario agregar */}
      {showAdd ? (
        <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/30 p-3 space-y-2">
          <ComboboxInput
            options={ESPECIALIDADES}
            value={addForm.nombre}
            onChange={v => setAddForm(p => ({ ...p, nombre: v }))}
            placeholder="Especialidad *"
            className={inputCls}
            freeText={false}
          />
          <input
            value={addForm.institucionFormadora}
            onChange={e => setAddForm(p => ({ ...p, institucionFormadora: e.target.value }))}
            placeholder="Institución formadora (ej. UNAM, BUAP...)"
            className={inputCls}
            maxLength={255}
          />
          <input
            value={addForm.cedulaEspecialidad}
            onChange={e => setAddForm(p => ({ ...p, cedulaEspecialidad: e.target.value }))}
            placeholder="Cédula de especialidad (opcional)"
            className={inputCls}
            maxLength={100}
          />
          {esAdmin && (
            <div className="space-y-2 pt-1 border-t border-primary-100">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Perfil profesional (determina qué funciones ve en el expediente)</label>
                <select
                  className={inputCls}
                  value={addForm.perfilProfesional}
                  onChange={e => {
                    const perfil = e.target.value as PerfilProfesional | '';
                    setAddForm(p => ({
                      ...p, perfilProfesional: perfil,
                      facultadPrescribir: perfil ? PERFILES_PRESCRIPCION_DEFAULT.includes(perfil as PerfilProfesional) : p.facultadPrescribir,
                    }));
                  }}
                >
                  <option value="">Sin clasificar (ve todo, por defecto)</option>
                  {PERFIL_PROFESIONAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none">
                <input type="checkbox" checked={addForm.facultadPrescribir}
                  onChange={e => setAddForm(p => ({ ...p, facultadPrescribir: e.target.checked }))} />
                Facultad de prescripción (habilita recetas y medicamentos activos)
              </label>
            </div>
          )}
          <p className="text-xs text-text-secondary">
            Datos obligatorios ante COFEPRIS para la emisión de recetas digitales.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAdd}
              disabled={addMut.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              <Plus size={12} /> Agregar
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setAddForm(emptyAdd); }}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 w-full px-3 py-2.5 rounded-xl border border-dashed border-gray-200 text-sm text-gray-500 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/30 transition-colors"
        >
          <Plus size={15} /> Agregar especialidad
        </button>
      )}
    </div>
  );
}
